import { For, Icon, If, onCleanup, onMount, reactive } from 'cdui-js';
import { getErrorFieldLabel } from '../../errorKeyNormalize';
import { pState } from '../../store';
import { EditErrorInfo } from './EditErrorInfo';
import dayts from '@china-difi/lib/date';
import i18n from '../../../../../../i18n';
import { replaceTemplate } from '@china-difi/lib/template';
import { finishOpenAccount } from '../../showSubmitSuccessModal';
import { LastNextBtns } from '../../components/LastNextBtns';

export type ReviewingProps = {
  /** 覆盖"完成"按钮默认行为（默认 finishOpenAccount：H5 登出 / App FINISHOPEN） */
  onDone?: () => void;
};

export const Reviewing = (props: ReviewingProps) => {
  const R = i18n.OpenProgress.Transfer.Review;
  const state = reactive({
    list: [
      {
        status: 1,
        title: R.StepReviewing,
        desc: replaceTemplate(R.StepReviewingDesc, {
          date: dayts.format(Date.now() + 72 * 60 * 60 * 1000),
        }),
        icon: 'account-open-review',
      },
      { status: 0, title: R.StepFundConfirm, desc: '', icon: 'fund-confirming' },
      { status: 0, title: R.StepSuccess, desc: '', icon: 'open-account-success' },
    ],
    showEdit: false,
  });

  let errors = [];

  if (pState.auditInfo?.status === 'BACK') {
    errors = JSON.parse(pState.auditInfo?.opinion || {});
  }

  const handleDone = () => {
    props.onDone ? props.onDone() : finishOpenAccount();
  };

  // 浏览器回退：与页底「完成」一致，退出开户（勿退回银行卡确认）
  onMount(() => {
    const pushGuard = () => {
      window.history.pushState({ __difiOpenReviewingExit: 1 }, '', window.location.href);
    };
    pushGuard();

    const onPopState = () => {
      // 修改资料浮层打开时：先关浮层，不退出开户
      if (state.showEdit) {
        pushGuard();
        state.showEdit = false;
        return;
      }
      pushGuard();
      handleDone();
    };
    window.addEventListener('popstate', onPopState);
    onCleanup(() => {
      window.removeEventListener('popstate', onPopState);
    });
  });

  const SuccessStatus = () => (
    <>
      <div class="flex justify-center">
        <svg width="60" height="60" viewBox="0 0 60 60" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_424_900)">
            <path
              d="M0 30C0 37.9565 3.16071 45.5871 8.7868 51.2132C14.4129 56.8393 22.0435 60 30 60C37.9565 60 45.5871 56.8393 51.2132 51.2132C56.8393 45.5871 60 37.9565 60 30C60 22.0435 56.8393 14.4129 51.2132 8.7868C45.5871 3.16071 37.9565 0 30 0C22.0435 0 14.4129 3.16071 8.7868 8.7868C3.16071 14.4129 0 22.0435 0 30Z"
              fill="#46DD93"
            />
            <path
              d="M24.0851 41.2626C24.4844 41.7021 25.0413 41.9659 25.6343 41.9965C26.2273 42.0272 26.8083 41.8221 27.2508 41.4261L27.3543 41.3286L42.8538 25.5786C43.2633 25.1625 43.4951 24.6034 43.5 24.0196C43.505 23.4357 43.2828 22.8728 42.8804 22.4498C42.4779 22.0267 41.9269 21.7767 41.3435 21.7524C40.7601 21.7282 40.1902 21.9317 39.7541 22.3199L39.6468 22.4219L25.8153 36.4761L19.6653 29.7059C19.2807 29.2824 18.7492 29.0214 18.179 28.9758C17.6087 28.9303 17.0425 29.1036 16.5956 29.4606L16.4876 29.5529C16.0639 29.9375 15.8027 30.4692 15.7571 31.0396C15.7116 31.61 15.8851 32.1764 16.2423 32.6234L16.3346 32.7314L24.0851 41.2626Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_424_900">
              <rect width="60" height="60" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <div class="flex flex-column align-center">
        <div class="color-D font-s-L font-bold margin-y-XS">{R.AppliedTitle}</div>
        <div class="text-align-center" style={{ width: '300px' }}>
          {R.AppliedDesc}
        </div>
      </div>
    </>
  );

  const ErrorStatus = () => (
    <>
      <div class="flex flex-column align-center">
        <svg width="60" height="60" viewBox="0 0 60 60" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_517_1210)">
            <path
              d="M0 30C0 37.9565 3.16071 45.5871 8.7868 51.2132C14.4129 56.8393 22.0435 60 30 60C37.9565 60 45.5871 56.8393 51.2132 51.2132C56.8393 45.5871 60 37.9565 60 30C60 22.0435 56.8393 14.4129 51.2132 8.7868C45.5871 3.16071 37.9565 0 30 0C22.0435 0 14.4129 3.16071 8.7868 8.7868C3.16071 14.4129 0 22.0435 0 30Z"
              fill="#FF4E4E"
            />
            <path
              d="M30 36.2688C29.0233 36.2688 28.2177 35.504 28.167 34.5286L27.3004 17.8625C27.22 16.3164 28.4519 15.0188 30 15.0188C31.5481 15.0188 32.78 16.3164 32.6996 17.8625L31.833 34.5286C31.7823 35.504 30.9767 36.2688 30 36.2688ZM30 44.9813C29.0933 44.9813 28.3283 44.6838 27.705 44.0888C27.11 43.4938 26.8125 42.7146 26.8125 41.7513C26.8125 40.788 27.1242 40.0088 27.7475 39.4138C28.3708 38.8188 29.1217 38.5213 30 38.5213C30.8783 38.5213 31.6292 38.8188 32.2525 39.4138C32.8758 40.0088 33.1875 40.788 33.1875 41.7513C33.1875 42.7146 32.8758 43.4938 32.2525 44.0888C31.6292 44.6838 30.8783 44.9813 30 44.9813Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_517_1210">
              <rect width="60" height="60" fill="white" />
            </clipPath>
          </defs>
        </svg>
        <div class="color-D font-s-L font-bold margin-y-XS">{R.FailedTitle}</div>
      </div>

      {pState.auditInfo?.status === 'BACK' && (
        <div class="margin-t padding-x-XXL">
          <div class="bg-c-XD round padding">
            {errors.map((item) => {
              return (
                <div class="padding-y-XXS flex justify-space-between gap-12px align-start">
                  <div class="color font-s-S">{getErrorFieldLabel(item.key)}</div>
                  <div class="color-error font-s-S text-right">{item.message}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div class="padding-x-L margin-y-XXL">
      <div class="flex flex-column margin-b-XXL">
        {['BACK', 'REFUSED'].includes(pState.auditInfo?.status) ? <ErrorStatus /> : <SuccessStatus />}
      </div>

      <div class="padding-x-XXL">
        <div class="color-D font-bold margin-b-L">{R.ProgressTitle}</div>

        <For each={state.list}>
          {(item, index) => {
            let borderClass = '';
            let iconBorderColor = 'border-c-L';
            let iconBgColor = 'bg-c-XD';

            if (index() !== 2) {
              borderClass = item.status ? 'border-l border-c-primary' : 'border-l';
            }

            if (item.status) {
              iconBorderColor = 'border-c-secondary';
              iconBgColor = 'bg-c';
            }

            return (
              <div class="flex gap-8px" style={{ position: 'relative' }}>
                <div
                  class={`flex align-center justify-center border ${iconBorderColor} ${iconBgColor}`}
                  style={{ width: '40px', height: '40px', 'border-radius': '50%', position: 'absolute', left: 0 }}
                >
                  <Icon name={item.icon} class={`icon-s ${item.status ? 'icon-stroke-blue' : 'icon-stroke-D'}`} />
                </div>

                <div
                  class={borderClass}
                  style={{ 'margin-top': '10px', height: '80px', 'padding-left': '40px', 'margin-left': '20px' }}
                >
                  <div class="flex color-D font-s align-center">
                    {/* 开户资料审核中 */}
                    {item.title}
                    <If when={item.status}>
                      <div class="color-primary font-s-XS margin-l flex align-center">
                        <span
                          class="margin-r-XXS flex-shrink-0"
                          style={{
                            width: '6px',
                            height: '6px',
                            'border-radius': '50%',
                            display: 'inline-block',
                            background: 'currentColor',
                          }}
                        ></span>
                        {i18n.OpenProgress.Common.InProgress}
                      </div>
                    </If>
                  </div>
                  <div>{item.desc}</div>
                </div>
              </div>
            );
          }}
        </For>
      </div>

      <If when={pState.auditInfo?.status === 'BACK'}>
        <button
          class="primary-btn btn-rounded width-100"
          onclick={() => {
            state.showEdit = true;
          }}
        >
          {R.GoEdit}
        </button>
      </If>

      {/* 固定底栏：与开户流程上一步/下一步同一套 Portal，小屏始终可见 */}
      <If when={pState.auditInfo?.status !== 'BACK'}>
        <LastNextBtns hideLast nextLabel={i18n.OpenProgress.Common.Done} next={handleDone} />
      </If>

      <If when={state.showEdit}>
        <EditErrorInfo
          close={() => {
            state.showEdit = false;
          }}
        />
      </If>
    </div>
  );
};
